import { useEffect, useState } from "react";

import { currentSession, signOut, type Session } from "./auth/session.js";
import { AdminRequests } from "./AdminRequests.js";
import { SignIn } from "./SignIn.js";
import { WorkspaceCycle } from "./WorkspaceCycle.js";
import { WorkspaceStatus } from "./WorkspaceStatus.js";

/**
 * The shell around the seven steps.
 *
 * **Three states, and which one you see is decided by the token.** Signed out
 * gets the sign-in form; signed in with no tenant claim gets the real workspace
 * status from the API; signed in with a tenant gets the rail. That is the same
 * split the back end enforces — `withSessionContext` serves the two routes a
 * pending session may reach, `withTenantContext` serves the rest — so the shell
 * cannot offer a screen the API would refuse.
 *
 * Approved sessions load the workspace, current cycle, artifacts and derived
 * decision state from the tenant-scoped API. The route only decides what to
 * render; every actual tenant refusal remains at the API edge.
 */

export const App = (): React.JSX.Element => {
  // `undefined` is "not looked yet" and `null` is "looked, nobody home". A
  // single nullable would render the sign-in form for a frame on every reload,
  // which reads as being signed out.
  const [session, setSession] = useState<Session | undefined | null>(undefined);

  useEffect(() => {
    let live = true;
    void currentSession().then((found) => {
      if (live) setSession(found ?? null);
    });
    return () => {
      live = false;
    };
  }, []);

  if (session === undefined) {
    return (
      <main className="shell" aria-busy="true">
        <p>Loading…</p>
      </main>
    );
  }

  if (session === null) {
    return <SignIn onSignedIn={setSession} />;
  }

  return (
    <main className="shell">
      <header>
        <h1>AI Build Platform</h1>
        <div className="identity">
          <span>{session.email}</span>
          {/* `ACC-004-AC3`. Reachable from every signed-in state, including
              the pending one — a user who cannot get in must still be able to
              get out. */}
          <button
            type="button"
            className="signout"
            onClick={() => {
              signOut();
              // A signed-out URL is not an open workspace step. Clearing it
              // lets the next fresh session resume from the server's saved
              // `currentStep` instead of mistaking the previous session's path
              // for a new deep-link request (`DSC-004-AC1`).
              window.history.replaceState(null, "", "/");
              setSession(null);
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {session.tenantId === "_platform" ? (
        <AdminRequests />
      ) : session.tenantId === undefined ? (
        <WorkspaceStatus />
      ) : (
        <WorkspaceCycle tenantId={session.tenantId} />
      )}
    </main>
  );
};
